import './SupportShareHint.scss';
import { CSSProperties, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { MdChatBubbleOutline, MdMic } from 'react-icons/md';
import Button from '@srm/ui/button';
import type { SupportPopupPlacement } from '@molecules/SupportChatPreview';
import { userInput } from '@providers/ScreenShareProvider/useRemoteControlInputBlock';

export interface ISupportShareHintProps {
    /** Голос доступен — подсказка и про микрофон */
    voice?: boolean;
    /** «Понятно», щелчок вне подсказки и Escape */
    onClose: () => void;
    placement?: SupportPopupPlacement;
    className?: string;
    style?: CSSProperties;
}

const SUPPORT_SHARE_HINT_TEXT = 'Напишите специалисту или включите микрофон, чтобы поговорить';
const SUPPORT_SHARE_HINT_CHAT_TEXT = 'Напишите специалисту в чат — он ответит здесь же';

/**
 * Подсказка-якорь у кнопок «Чат» и «Микрофон» при первом показе экрана (один раз на пользователя): что с поддержкой
 * можно переписываться и говорить. Закрывается «Понятно», щелчком вне её и Escape самого пользователя (команды
 * удалённого управления — синтетические события, подсказку сотрудник и не видит); фокус не забирает — это пометка, а
 * не диалог (диктору её текст объявляет тот, кто её показал).
 */
const SupportShareHint = ({ voice = true, onClose, placement = 'below', className, style }: ISupportShareHintProps) => {
    const root = useRef<HTMLDivElement>(null);
    const close = useRef(onClose);
    close.current = onClose;
    useEffect(() => {
        const outside = (event: PointerEvent) => {
            if (userInput.is(event) && !root.current?.contains(event.target as Node)) close.current();
        };
        const escape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && userInput.is(event)) close.current();
        };
        document.addEventListener('pointerdown', outside, true);
        document.addEventListener('keydown', escape);
        return () => {
            document.removeEventListener('pointerdown', outside, true);
            document.removeEventListener('keydown', escape);
        };
    }, []);
    return (
        <div
            ref={root}
            className={clsx('support-share-hint', className)}
            role="note"
            aria-label="Подсказка"
            data-placement={placement}
            data-remote-control-exclude
            data-testid="support-share-hint"
            style={style}
        >
            <span className="support-share-hint__icons" aria-hidden>
                <MdChatBubbleOutline size={18} />
                {voice && <MdMic size={18} />}
            </span>
            <p className="support-share-hint__text">{voice ? SUPPORT_SHARE_HINT_TEXT : SUPPORT_SHARE_HINT_CHAT_TEXT}</p>
            <Button size="sm" variant="outline-neutral" className="support-share-hint__ok" onClick={onClose}>
                Понятно
            </Button>
        </div>
    );
};

export default SupportShareHint;
